/**
 * PreviousInvoicesSection
 *
 * Renders the work order's voided / refunded / credited invoices in a
 * subtle history list. The active invoice is rendered by
 * InvoiceSummaryCard and is intentionally excluded here so the two
 * components don't duplicate.
 *
 * Hidden entirely when there are no historical invoices.
 */

import { formatCentsForDisplay as formatPrice } from '@/utils/money';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Stack, Typography } from '@mui/material';
import type { WorkOrderInvoiceHistoryEntry } from '@/hooks/useInvoices';
import { formatDate } from '@/utils/locale';


const REVERSED_STATUSES = new Set(['void', 'refunded', 'credited']);

interface PreviousInvoicesSectionProps {
  invoices: WorkOrderInvoiceHistoryEntry[];
}

export function PreviousInvoicesSection({ invoices }: PreviousInvoicesSectionProps) {
  const { t } = useTranslation('invoices');

  const historical = invoices.filter((inv) => REVERSED_STATUSES.has(inv.status));
  if (historical.length === 0) return null;

  return (
    <Box data-testid="previous-invoices-section" sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        {t('history.title')}
      </Typography>
      <Stack spacing={1}>
        {historical.map((inv) => (
          <Box
            key={inv.id}
            data-testid="previous-invoice-row"
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', textDecoration: 'line-through', color: 'text.secondary' }}
                >
                  {inv.invoiceNumber}
                </Typography>
                <Chip
                  size="small"
                  label={t(`status.${inv.status}`)}
                  color={inv.status === 'void' ? 'default' : 'default'}
                  variant="outlined"
                />
              </Stack>
              {inv.voidedAt ? (
                <Typography variant="caption" color="text.secondary">
                  {formatDate(inv.voidedAt)}
                  {inv.voidReason ? ` — ${inv.voidReason}` : ''}
                </Typography>
              ) : null}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
              {formatPrice(inv.totalCents)}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
